import type { PricingCampaignsScope } from '@/entities/pricing-campaign'
import type {
	SRT_ColumnDef,
	SRT_Row,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type { OptimizationGroupModel } from '@/shared/generated'
import type { PricingCampaignTableRow } from '../providers/PricingCampaignsProvider'

import React, { use, useEffect, useMemo, useState } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { formatDistanceToNow, isToday } from 'date-fns'
import { indexBy, prop } from 'lodash/fp'
import {
	ArrowDownUpIcon,
	ArrowRightIcon,
	ChevronRightIcon,
	CopyIcon,
	LayersIcon,
	Link2Icon,
	ListCollapseIcon,
	PlusIcon,
	PuzzleIcon,
	Trash2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApplyPricingCampaignTemplate } from '@/features/pricing-campaign/apply-template'
import { CopyPricingCampaignSettings } from '@/features/pricing-campaign/copy-settings'
import { CopyPricingCampaign } from '@/features/pricing-campaign/copy-to-og'
import { CreatePricingCampaign } from '@/features/pricing-campaign/create'
import { DeletePricingCampaign } from '@/features/pricing-campaign/delete'
import { DuplicatePricingCampaign } from '@/features/pricing-campaign/duplicate'
import { ReassignPricingCampaign } from '@/features/pricing-campaign/reassign-to-og'
import { SavePricingCampaignAsTemplate } from '@/features/pricing-campaign/save-as-template'

import {
	getOptimizationsMap,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import {
	EngineBadge,
	getPricingCampaignEngine,
	GlobalPcBadge,
	globalPricingCampaignProductsLink,
	isGlobalPricingCampaign,
	loclaPricingCampaignProductsLink,
	ogNameAndOrder,
} from '@/entities/pricing-campaign'
import { UsernameById } from '@/entities/user'

import { PercentageValueText } from '@/shared/components/common/PercentageValueText'
import {
	createSRTColumnHelper,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { SRT_ExpandAllButton } from '@/shared/components/master-table/components/buttons/SRT_ExpandAllButton'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import { DropdownMenuItem } from '@/shared/components/ui/dropdown-menu'
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '@/shared/components/ui/hover-card'
import {
	LinkButton,
	linkButtonVariants,
} from '@/shared/components/ui/link-button'
import { RelativeTimeCard } from '@/shared/components/ui/relative-time-card'
import { Separator } from '@/shared/components/ui/separator'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { ApplyAction } from '@/shared/generated'
import { useDisableVirtualization } from '@/shared/hooks/useDisableVirtualization'
import { getLangCode, getMasterTableLocale, useLocale } from '@/shared/i18n'
import { ampli } from '@/shared/lib/ampli'
import { useDrawers } from '@/shared/lib/drawer-manager'
import { useModals } from '@/shared/lib/modal-manager'
import { cn } from '@/shared/lib/utils'
import { intlFormat } from '@/shared/tools/dates'
import { formatNumber } from '@/shared/tools/locale'

import { PricingCampaignsContext } from '../providers/PricingCampaignsProvider'
import OptimizationGroupsTooltipList from '../ui/OptimizationGroupsTooltipList'

type PricingCampaignsTableProps = {
	data: PricingCampaignTableRow[]
	scope?: PricingCampaignsScope
	isLoading?: boolean
}

export const usePricingCampaignsTable = ({
	data,
	scope,
	isLoading,
}: PricingCampaignsTableProps) => {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SRT_SortingState>([
		{ desc: false, id: 'order' },
	])
	useEffect(() => {
		ampli.settingsPricingCampaignsTableSortBy({
			value: sorting.map(prop('id')).filter(Boolean) as string[],
		})
	}, [sorting])
	const summary = useMemo(
		() => ({
			items: data.length,
			alerts: data.reduce(
				(acc: number, curr: PricingCampaignTableRow) =>
					acc + (curr.alerts_count || 0),
				0
			),
			products: data.reduce(
				(acc: number, curr: PricingCampaignTableRow) =>
					acc + (curr.products_count || 0),
				0
			),
		}),
		[data]
	)
	const columns = useColumns({ scope, summary })
	const disableVirtualization = useDisableVirtualization()

	return useMasterReactTable<PricingCampaignTableRow>({
		localization: getMasterTableLocale(),
		data,
		columns,
		getRowId: (row, index) => row.campaign?.id || String(index),
		state: { sorting, isLoading },
		initialState: {
			columnVisibility: scope?.optimization_group_id
				? {
						optimization_groups: false, //hide this column by default
						'srt-row-expand': false,
					}
				: { 'srt-row-expand': false },
			expanded: true,
			grouping: ['campaign.optimization_group_id'],
		},
		divideX: false,
		enableGrouping: true,
		enableHeaderTooltip: true,
		enableColumnActions: false,
		enableTableFooter: true,
		enableRowActions: true,
		groupedColumnMode: 'remove',
		positionActionsColumn: 'last',
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		sortingFns: {
			ogNameAndOrder,
		},
		enableColumnFilters: true,
		enableColumnPinning: false,
		enableSorting: true,
		layoutMode: 'grid',
		displayColumnDefOptions: {
			'srt-row-actions': {
				header: '',
			},
		},
		renderRowActionMenuItems(props) {
			const pricingCampaignId = props.row.original.campaign.id
			const isGlobal = isGlobalPricingCampaign(props.row.original.campaign)

			return [
				<ApplyPricingCampaignTemplate
					key='apply-template'
					action={ApplyAction.ReplacePricingCampaignSettings}
					pricingCampaignId={pricingCampaignId}
				>
					<DropdownMenuItem>
						<PuzzleIcon /> {t('pc.action.apply_template')}
					</DropdownMenuItem>
				</ApplyPricingCampaignTemplate>,
				<SavePricingCampaignAsTemplate
					key='save-as-template'
					pricingCampaignId={pricingCampaignId}
				>
					<DropdownMenuItem>
						<PlusIcon />
						{t('settings.action.save_as_template', {
							defaultValue: 'Save as template',
						})}
					</DropdownMenuItem>
				</SavePricingCampaignAsTemplate>,
				<ReassignPricingCampaign
					key='reassign'
					pricingCampaignsIds={[pricingCampaignId]}
				>
					<DropdownMenuItem
						disabled={isGlobal}
						onClick={() => ampli.pcReassignClicked()}
					>
						<Link2Icon /> {t('pc.action.reassign')}
					</DropdownMenuItem>
				</ReassignPricingCampaign>,
				<CopyPricingCampaignSettings
					key='copy-settings'
					pricingCampaignId={pricingCampaignId}
				>
					<DropdownMenuItem>
						<CopyIcon />
						{t('pc.action.copy_settings', {
							defaultValue: 'Copy settings to ...',
						})}
					</DropdownMenuItem>
				</CopyPricingCampaignSettings>,
				<CopyPricingCampaign
					key='copy'
					pricingCampaignsIds={[pricingCampaignId]}
				>
					<DropdownMenuItem disabled={isGlobal}>
						<CopyIcon /> {t('pc.action.copy', { defaultValue: 'Copy to ...' })}
					</DropdownMenuItem>
				</CopyPricingCampaign>,
				<DuplicatePricingCampaign
					key='duplicate'
					pricingCampaignId={pricingCampaignId}
					destinationIds={[
						props.row.original.campaign?.optimization_group_id ?? null,
					]}
					destinationType='optimization_group'
				>
					<DropdownMenuItem onClick={() => ampli.pcDuplicateClicked()}>
						<LayersIcon /> {t('general_duplicate')}
					</DropdownMenuItem>
				</DuplicatePricingCampaign>,
				<DeletePricingCampaign
					key='delete'
					pricingCampaignId={pricingCampaignId}
				>
					<DropdownMenuItem
						variant='destructive'
						onClick={() => ampli.pcDeleteClicked()}
					>
						<Trash2Icon /> {t('general_delete')}
					</DropdownMenuItem>
				</DeletePricingCampaign>,
			]
		},
		enableVirtualization: !disableVirtualization,
		getRowHeight(row) {
			if (isLoading) return 36

			return row?.depth === 0 ? 65 : 36
		},
		renderGroup: ({ row }) => <TableOptimzationGroupGroup row={row} />,
		onSortingChange: setSorting,
	})
}

const columnHelper = createSRTColumnHelper<PricingCampaignTableRow>()

const useColumns = ({
	summary,
	scope,
}: {
	summary: { items: number; alerts: number; products: number }
	scope?: PricingCampaignsScope
}) => {
	const { t } = useTranslation()

	const isAllGlobal = !scope?.optimization_group_id

	return useMemo(
		() =>
			[
				columnHelper.display({
					id: 'control',
					enableHeaderTooltip: false,
					enableColumnActions: false,
					enableColumnDragging: false,
					enableColumnOrdering: false,
					enableSorting: false,
					header: '',
					Header: ({ table }) => <SRT_ExpandAllButton table={table} />,
					Cell: ({ row }) => (
						<ControlCell row={row} isAllGlobal={isAllGlobal} />
					),
					size: 42,
				}),
				columnHelper.accessor('campaign.name', {
					header: t('pc_name'),
					Cell: ({ row, cellValue }) => (
						<PricingCampaignNameCell row={row} cellValue={cellValue} />
					),
					enableColumnFilter: true,
					meta: {
						label: t('pc_name'),
						placeholder: t('pc_name'),
						variant: 'text',
					},
				}),
				columnHelper.accessor('order', {
					header: t('general_priority'),
					Cell: ({ renderedCellValue }) => (
						<Badge className='font-mono text-foreground no-underline'>
							{renderedCellValue}
						</Badge>
					),
					sortingFn: 'ogNameAndOrder',
					size: 70,
				}),
				columnHelper.accessor('campaign.engine', {
					header: t('pricing_engine'),
					Cell: ({ row }) => <PricingCampaignEngineCell row={row} />,
					size: 180,
				}),
				columnHelper.accessor('products_count', {
					header: t('items_in_pc'),
					Cell: ({ row, cellValue }) => (
						<ProductsCountCell row={row} cellValue={cellValue} scope={scope} />
					),
					Footer: () => {
						const { t } = useTranslation()

						return (
							<div>
								<div className='text-sm text-muted-foreground'>
									{t('total', { defaultValue: 'Total' })}:
								</div>
								<div className='font-bold'>
									{formatNumber(summary.products)}
								</div>
							</div>
						)
					},
					size: 120,
				}),
				columnHelper.accessor('campaign.optimization_group_id', {
					id: 'campaign.optimization_group_id',
					header: t('items_in_pc'),
					getGroupingValue(row) {
						return row.campaign.optimization_group_id
					},
					Cell: ({ renderedCellValue }) => (
						<div className='truncate'>{renderedCellValue}</div>
					),
					size: 120,
				}),
				// Visible only for global pricing campaigns
				!scope?.optimization_group_id &&
					columnHelper.accessor('optimization_groups', {
						header: t('ogs', { defaultValue: 'OGs' }),
						Cell: ({ row, cellValue }) => (
							<OptimizationGroupsCell row={row} cellValue={cellValue} />
						),
						size: 70,
					}),

				columnHelper.accessor('alerts_count', {
					header: t('alerts', { defaultValue: 'Alerts' }),
					Cell: ({ row, cellValue }) => (
						<AlertsCell row={row} cellValue={cellValue} />
					),
					Footer: () => {
						const { t } = useTranslation()

						return (
							<div>
								<div className='text-sm text-muted-foreground'>
									{t('total', { defaultValue: 'Total' })}:
								</div>
								<div className='font-bold'>{formatNumber(summary.alerts)}</div>
							</div>
						)
					},
					size: 80,
				}),
				columnHelper.accessor(prop('campaign.id'), {
					id: 'period',
					header: t('period'),
					Cell: ({ row }) => <ActivityPeriodCell row={row} />,
					size: 200,
				}),
				columnHelper.accessor('campaign.updated_at', {
					header: t('last_modified_at'),
					Cell: ({ row }) => <LastModifiedCell row={row} />,
					size: 200,
				}),
			].filter(Boolean) as SRT_ColumnDef<PricingCampaignTableRow, any>[],
		[isAllGlobal, summary]
	)
}

function ControlCell({
	row,
	isAllGlobal,
}: {
	row: SRT_Row<PricingCampaignTableRow>
	isAllGlobal: boolean
}) {
	const id = row.original.campaign.id
	const { campaign } = row.original
	const { selected, setSelected } = use(PricingCampaignsContext)
	const isSelected = selected.includes(id)
	const isDisabled = isGlobalPricingCampaign(campaign) && !isAllGlobal

	return (
		<Checkbox
			disabled={isDisabled}
			checked={isSelected}
			onChange={() =>
				setSelected(
					isSelected
						? selected.filter((item) => item !== id)
						: [...selected, id]
				)
			}
		/>
	)
}
function AlertsCell({
	row,
	cellValue,
}: {
	row: SRT_Row<PricingCampaignTableRow>
	cellValue: number | undefined
}) {
	const value = cellValue
	const { campaign, optimization_groups } = row.original
	const { openDrawer } = useDrawers()
	const isFetching = useIsFetching({
		queryKey: [
			'pricing-alerts',
			{
				group_by: 'pricing_campaign_id',
				optimization_group_id: campaign.optimization_group_id || undefined,
			},
		],
	})
	if (isFetching && !value) {
		return <Skeleton className='h-4 w-11' />
	}
	const isGlobal = isGlobalPricingCampaign(campaign)
	const optimizationGroupIds = isGlobal
		? optimization_groups
		: [campaign.optimization_group_id]

	return value ? (
		<Badge
			variant='destructive'
			className='cursor-pointer font-mono'
			interactive
			onClick={() => {
				ampli.alertsOpenClicked({
					alerts_of: 'pc',
				})
				openDrawer('PRICING_ALERTS', {
					type: 'byPricingCampaign',
					pricingCampaign: campaign,
					optimizationGroupIds: optimizationGroupIds as string[],
					drawerClassName: isGlobal
						? 'global-pc-alerts-eastside'
						: 'pc-alerts-eastside',
				})
			}}
		>
			{formatNumber(value || 0)}
		</Badge>
	) : (
		'-'
	)
}

function OptimizationGroupsCell({
	row,
	cellValue,
}: {
	row: SRT_Row<PricingCampaignTableRow>
	cellValue: string[] | undefined
}) {
	const value = cellValue
	const navigate = useNavigate()
	const groupsQuery = useOptimizationGroupsQuery<
		Record<string, OptimizationGroupModel>
	>({ select: indexBy<OptimizationGroupModel>('id') })

	return row.original.campaign?.optimization_group_id ? (
		<Link
			to='/p/og'
			search={{ 'group.id__in': row.original?.campaign?.optimization_group_id }}
			className={cn(linkButtonVariants(), 'block truncate')}
		>
			{groupsQuery.data?.[row.original?.campaign?.optimization_group_id]?.name}
		</Link>
	) : value?.length ? (
		<HoverCard>
			<HoverCardTrigger>
				<Badge
					interactive
					className='cursor-pointer'
					onClick={() =>
						navigate({ to: `/p/og?group.id__in=${value.join(',')}` })
					}
				>
					{formatNumber(value?.length || 0)}
				</Badge>
			</HoverCardTrigger>
			<HoverCardContent className='max-h-72 overflow-y-auto p-0'>
				<OptimizationGroupsTooltipList ids={value} />
			</HoverCardContent>
		</HoverCard>
	) : (
		'-'
	)
}
function PricingCampaignNameCell({
	row,
	cellValue,
}: {
	row: SRT_Row<PricingCampaignTableRow>
	cellValue: string
}) {
	const { showModal } = useModals()
	const { campaign } = row.original

	return (
		<div className='flex items-center gap-2'>
			<LinkButton
				variant='brand'
				className='text-left'
				onClick={() => {
					ampli.settingsPricingCampaignsViewClick({
						location: 'pricing-camapigns-table',
					})
					showModal('PRICING_CAMPAIGN_MODAL', {
						pricingCampaignId: campaign.id,
						optimizationGroupId: campaign.optimization_group_id,
						scenarioId: campaign.scenario_id,
					})
				}}
			>
				{cellValue}
			</LinkButton>
		</div>
	)
}
function ProductsCountCell({
	row,
	cellValue,
	scope,
}: {
	row: SRT_Row<PricingCampaignTableRow>
	scope?: PricingCampaignsScope
	cellValue: number
}) {
	const value = cellValue
	const { campaign, assortment_share } = row.original
	const isFetching = useIsFetching({ queryKey: ['products-counts-by-pc'] })
	if (isFetching) {
		return <Skeleton className='h-4 w-9' />
	}

	return (
		<LinkButton variant='brand' asChild className='font-mono'>
			<Badge asChild variant='link'>
				<Link
					to={
						scope?.optimization_group_id
							? loclaPricingCampaignProductsLink(campaign, scope)
							: globalPricingCampaignProductsLink(campaign)
					}
				>
					<PercentageValueText
						hidePercentage
						value={value}
						percentage={assortment_share || 0}
					/>
				</Link>
			</Badge>
		</LinkButton>
	)
}
function PricingCampaignEngineCell({
	row,
}: {
	row: SRT_Row<PricingCampaignTableRow>
}) {
	const { t } = useTranslation()

	const { campaign } = row.original
	const engine = getPricingCampaignEngine(campaign)

	return (
		<div className='flex items-center gap-1'>
			<EngineBadge engine={engine}>
				{t(engine, { defaultValue: engine })}
			</EngineBadge>
			{isGlobalPricingCampaign(campaign) && <GlobalPcBadge />}
		</div>
	)
}
function ActivityPeriodCell({
	row,
}: {
	row: SRT_Row<PricingCampaignTableRow>
}) {
	const { campaign } = row.original
	if (!campaign.start_date && !campaign.end_date) {
		return null
	}
	const { start_date, end_date } = campaign

	return (
		<div className='flex items-center space-x-1 font-normal'>
			{start_date && <span>{intlFormat(new Date(start_date + 'Z'))}</span>}
			{(start_date || end_date) && (
				<ArrowRightIcon className='size-4 shrink-0' />
			)}
			{end_date && <span>{intlFormat(new Date(end_date + 'Z'))}</span>}
		</div>
	)
}
function LastModifiedCell({ row }: { row: SRT_Row<PricingCampaignTableRow> }) {
	const { t } = useTranslation()
	const currentLocale = useLocale()
	const { campaign } = row.original
	const date = campaign.updated_at && new Date(campaign.updated_at + 'Z')

	return (
		<div className='flex items-center justify-between text-sm text-muted-foreground'>
			<div className='truncate'>
				{date && (
					<RelativeTimeCard date={date} locale={getLangCode(currentLocale)}>
						<span>
							{isToday(date)
								? formatDistanceToNow(date, { addSuffix: true })
								: intlFormat(date)}
						</span>
					</RelativeTimeCard>
				)}
				{campaign.updated_by && date && ` ${t('by', { defaultValue: 'by' })} `}
				{campaign.updated_by ? (
					<UsernameById userId={campaign.updated_by} />
				) : (
					'-'
				)}
			</div>
		</div>
	)
}

function TableOptimzationGroupGroup({
	row,
}: {
	row: SRT_Row<PricingCampaignTableRow>
}) {
	const { t } = useTranslation()

	const optimizations = useOptimizationsQuery({
		select: getOptimizationsMap,
	})
	const groupsQuery = useOptimizationGroupsQuery<
		Record<string, OptimizationGroupModel>
	>({ select: indexBy<OptimizationGroupModel>('id') })
	const { showModal } = useModals()
	const group = groupsQuery.data?.[row.groupingValue as string]
	const optimization = group ? optimizations.data?.[group?.id] : undefined
	const name = group?.name || t('global', { defaultValue: 'Global' })
	const navigate = useNavigate()

	return (
		<div className='flex h-16 items-start gap-4 pt-2'>
			<Button
				variant='ghost'
				size='icon-sm'
				data-expanded={row.getIsExpanded()}
				onClick={row.getToggleExpandedHandler()}
				className='group data-[expanded=true]:rotate-90'
			>
				<ChevronRightIcon className='opacity-50 group-hover:opacity-100' />
			</Button>
			<div className='flex flex-col gap-2'>
				<div className='flex items-center gap-2'>
					<h5 className='font-bold'>{name}</h5>
					{group?.id && (
						<LinkButton
							onClick={() => {
								navigate({ to: `/p/og/${group.id}/products` })
							}}
						>
							<ListCollapseIcon className='size-4' />
						</LinkButton>
					)}
				</div>
				<div className='flex h-full items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{row.subRows?.length} {t('general_pc')}
					</span>
					<Separator orientation='vertical' className='!h-4' />
					{group && (
						<>
							<span>
								{formatNumber(optimization?.total_products || 0)}{' '}
								{t('general_products')}
							</span>
							<Separator orientation='vertical' className='!h-4' />
						</>
					)}
					<LinkButton
						data-testid='pcs-priority-modal-trigger'
						onClick={() => {
							ampli.settingsPricingCampaignsReorderClick({
								location: 'table-group-header',
							})
							showModal('PRICING_CAMPAIGNS_PRIORITY', {
								optimizationGroupId: group?.id,
								globalPcSetId: row.original.campaign.global_pc_set_id || null,
								scenarioId: row.original.campaign.scenario_id || null,
							})
						}}
					>
						<ArrowDownUpIcon />{' '}
						{t('pc.change_priority', { defaultValue: 'Change priority' })}
					</LinkButton>
					<Separator orientation='vertical' className='h-4!' />
					<CreatePricingCampaign
						location='table-group-header'
						scope={{ optimizationGroupId: group?.id }}
					>
						<LinkButton data-testid='create-pc-modal-trigger'>
							<PlusIcon /> {t('general_create')}
						</LinkButton>
					</CreatePricingCampaign>
				</div>
			</div>
		</div>
	)
}
